import {
  Address,
  AloftArea,
  AloftPrice,
  MLSListing,
  ToolkitListingID,
  getLotAreaInSqft,
  isDefined,
  isUndefined,
  orUndefined,
  schIsoMonth,
  syntaxFNMA,
} from '@aloft/toolkit-shared';

import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { CompSelectionProperty } from '../types.ts';
import { addQualityTiers } from './addQualityTiers.ts';
import { addSimilarityScores } from './addSimilarityScores.ts';
import { areAddressesEqual } from './listingsToCompSelectionProps.ts';
import { normalizeRentalListingStatus } from './normalizeRentalListingStatus.ts';

const getMonthFromDateString = (dateString?: string) => {
  if (!dateString) return undefined;

  const [year, month] = dateString.split('-');
  if (!year || !month || year.length !== 4 || month.length !== 2) return undefined;

  return schIsoMonth.safeParse(`${year}-${month}`).data;
};

const buildRentalCompSelectionProperty = (
  listing: MLSListing,
  subject?: CompSelectionProperty,
  comps?: CompSelectionProperty[],
): CompSelectionProperty | undefined => {
  const subjectCoordinates =
    subject && subject.latitude && subject.longitude
      ? { lat: subject.latitude, lng: subject.longitude }
      : undefined;
  if (isUndefined(listing.UnparsedAddress)) return undefined;
  if (areAddressesEqual(listing.UnparsedAddress, subject?.address?.AddressLineText ?? ''))
    return undefined;

  const id = listing.ToolkitListingID ?? listing.ListingId;
  if (isUndefined(id) || id === '') return undefined;

  const leaseMonth = getMonthFromDateString(listing.CloseDate);

  const contractMonth = getMonthFromDateString(listing.PurchaseContractDate);
  const listingContractDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(
    getMonthFromDateString(listing.ListingContractDate),
  );

  const leaseDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(leaseMonth);
  const contractDate = orUndefined(syntaxFNMA.isoMonthToAloftDate)(contractMonth);

  const address: Address = {
    CityName: listing.City,
    StateCode: listing.StateOrProvince,
    PostalCode: listing.PostalCode,
    AddressLineText: listing.UnparsedAddress,
  };

  const subjectLatLong = subjectCoordinates
    ? { longitude: subjectCoordinates.lng, latitude: subjectCoordinates.lat }
    : undefined;

  const listingLatLong =
    isDefined(listing.Latitude) && isDefined(listing.Longitude)
      ? { longitude: listing.Longitude, latitude: listing.Latitude }
      : undefined;

  const distanceToSubject =
    isDefined(subjectLatLong) && isDefined(listingLatLong)
      ? +(proximityMiles(subjectLatLong, listingLatLong)?.toFixed(2) ?? 0)
      : undefined;

  const toolkitId = listing.ToolkitListingID;
  const isSelectedComp = toolkitId !== undefined && comps?.some((c) => c.listingID === toolkitId);

  const rentRate = AloftPrice.fromDollarLike(listing.ListPrice);

  return {
    t: isSelectedComp ? 'tk' : 'abode',
    id,
    address,
    gla: AloftArea.fromNumber(listing.LivingArea, 'sqft'),
    lotSize: getLotAreaInSqft(listing),
    leaseDate,
    contractDate,
    listingContractDate,
    rentRate,
    listPrice: rentRate,
    distanceToSubject,
    imageUrls: listing.Media?.map((m) => m.MediaURL),
    latitude: listing.Latitude,
    longitude: listing.Longitude,
    fullBaths: listing.BathroomsFull,
    halfBaths: listing.BathroomsHalf,
    bedroomCount: listing.BedroomsTotal,
    yearBuilt: listing.YearBuilt,
    bedrooms: listing.BedroomsTotal,
    basement: listing.BasementYN,
    garage: listing.GarageYN,
    quality: undefined,
    condition: undefined,
    cumulativeDaysOnMarket: listing.CumulativeDaysOnMarket || listing.DaysOnMarket,
    listingID: listing.ToolkitListingID || (listing.ListingId as ToolkitListingID),
    mlsStatus: normalizeRentalListingStatus(listing.MlsStatus) ?? listing.MlsStatus,
    reo: listing.SpecialListingConditions?.includes('REO'),
    shortSale: listing.SpecialListingConditions?.includes('Short Sale'),
    garageCount: listing.GarageSpaces,
    pool: listing.PoolPrivateYN,
    propertyType: listing.PropertySubType,
  };
};

export const rentalListingsToCompSelectionProps = (
  subject: CompSelectionProperty,
  comps: CompSelectionProperty[],
  listings: MLSListing[],
  hasArvOption: boolean,
): CompSelectionProperty[] => {
  const properties = listings
    .map((listing) => buildRentalCompSelectionProperty(listing, subject, comps))
    .filter(isDefined);

  addSimilarityScores(properties, subject);

  if (hasArvOption) {
    addQualityTiers(properties);
  }
  return properties;
};
